// frontend/src/hooks/useReferenceData.js
// Loads the roster and dropdown vocabulary once and shares it across forms.
import { useEffect, useState } from 'react';
import { getUsers, getMeta } from '../services/endpoints';
import {
    STATUSES, TICKET_STATUSES, TASK_TYPES, PRIORITIES, ACTIVITY_TYPES, isManagerRole,
} from '../constants/domain';

// Module-level cache: these lists change rarely, and every form needs them.
let cache = null;
let inflight = null;

async function load() {
    if (cache) return cache;
    if (inflight) return inflight;

    inflight = (async () => {
        const [usersRes, metaRes] = await Promise.all([
            getUsers().catch(() => ({ data: [] })),
            getMeta().catch(() => ({ data: {} })),
        ]);
        const meta = metaRes.data || {};
        cache = {
            users: usersRes.data || [],
            engineers: (usersRes.data || []).filter(u => u.role !== 'admin'),
            managers: (usersRes.data || []).filter(u => isManagerRole(u.role)),
            statuses: meta.statuses || STATUSES,
            ticketStatuses: meta.ticket_statuses || TICKET_STATUSES,
            taskTypes: meta.task_types || TASK_TYPES,
            priorities: meta.priorities || PRIORITIES,
            activityTypes: meta.activity_types || ACTIVITY_TYPES,
            mainFlow: meta.main_flow || [],
        };
        inflight = null;
        return cache;
    })();

    return inflight;
}

/** Clear the cache, e.g. after adding a user. */
export function invalidateReferenceData() {
    cache = null;
    inflight = null;
}

export default function useReferenceData() {
    const [data, setData] = useState(cache);
    const [loading, setLoading] = useState(!cache);

    useEffect(() => {
        let alive = true;
        if (cache) {
            setData(cache);
            setLoading(false);
            return () => { alive = false; };
        }
        load().then(d => {
            if (alive) {
                setData(d);
                setLoading(false);
            }
        }).catch(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    return {
        loading,
        users: data?.users || [],
        engineers: data?.engineers || [],
        managers: data?.managers || [],
        statuses: data?.statuses || STATUSES,
        ticketStatuses: data?.ticketStatuses || TICKET_STATUSES,
        taskTypes: data?.taskTypes || TASK_TYPES,
        priorities: data?.priorities || PRIORITIES,
        activityTypes: data?.activityTypes || ACTIVITY_TYPES,
        userName: (id) => (data?.users || []).find(u => u.id === id)?.full_name || '',
    };
}
